<script setup lang="ts">
import type { AccordionItem, FormSubmitEvent } from '#ui/types'
import { merge } from 'es-toolkit/compat'

const props = defineProps<{
  action: 'create' | 'edit'
  data: Partial<Bank>
  onSubmit: (event: FormSubmitEvent<Partial<Bank>>) => void
  openNestedCallback: (data: Partial<Bank>) => void
  title?: string
  disabledFields?: (keyof Bank | string)[]
  readonly?: boolean
}>()

const { user } = useUserSession()
const { userModule } = useModules()
const slideover = useSlideover()

const inert = computed(() => props.readonly || props.data?.immutable || !['admin', 'edit'].includes(userModule.value?.role))
const simple = ref(true)

const state = ref<Partial<Bank>>(props.data)

const banks$ = useBanks()

const utils = useBanksUtils()

const items = computed<AccordionItem[]>(() => {
  if (['create'].includes(props.action) && simple.value) {
    return [{ slot: 'simple', label: 'Creación rápida', icon: 'i-mdi-lightning-bolt', defaultOpen: true }]
  }
  return [
    {
      slot: 'general',
      label: 'General',
      icon: 'i-mdi-information',
      defaultOpen: true,
    },
    {
      slot: 'details',
      label: 'Detalles',
      icon: ICONS.banks,
      defaultOpen: true,
    },
    {
      slot: 'address',
      label: 'Dirección',
      icon: 'i-mdi-map-marker',
      defaultOpen: true,
    },
    {
      slot: 'data',
      label: 'Datos adicionales',
      icon: 'i-mdi-code-json',
      defaultOpen: false,
    },
  ].filter(item => !['data'].includes(item.slot) || state.value.data)
})

const loading = ref(false)

const onSubmitBank = async (event: FormSubmitEvent<Partial<Bank>>) => {
  event.data = merge(state.value, event.data) // WORKAROUND: UForm drops nested props on validation
  loading.value = true
  await props.onSubmit(event)
}
</script>

<template>
  <UDashboardSlideover prevent-close :ui="{ width: 'min-w-[40vw]' }">
    <template #title>
      <SlideoverTitle :title="title" :inert="inert" />
    </template>
    <UForm
      id="form.banks"
      :validate-on="['submit']"
      :schema="banks$.schema"
      :state="state"
      :inert="inert"
      @error="onFormError"
      @submit="onSubmitBank"
    >
      <UAccordion
        multiple
        :items="items"
        :ui="{ item: { base: 'py-1 px-2 space-y-2 text-sm' } }"
      >
        <template #simple>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(banks$.schema, 'name')"
          >
            <InputText
              v-model.lowercase="state.name"
              autofocus
              :disabled="disabledFields?.includes('name')"
            />
          </UFormGroup>

          <UFormGroup
            label="Código SWIFT"
            name="swift"
            :required="isRequired(banks$.schema, 'swift')"
          >
            <InputText
              v-model.defaultcase="state.swift"
              type="text"
              :disabled="disabledFields?.includes('swift')"
            />
          </UFormGroup>

          <UFormGroup
            label="Código de banco"
            name="bankCode"
            :required="isRequired(banks$.schema, 'bankCode')"
          >
            <InputText
              v-model.defaultcase="state.bankCode"
              type="text"
              :disabled="disabledFields?.includes('bankCode')"
            />
          </UFormGroup>

          <UFormGroup
            label="Número de sucursal"
            name="subsidiaryNumber"
            :required="isRequired(banks$.schema, 'subsidiaryNumber')"
          >
            <InputText
              v-model.defaultcase="state.subsidiaryNumber"
              type="text"
              :disabled="disabledFields?.includes('subsidiaryNumber')"
            />
          </UFormGroup>

          <SlideoverButtonExpand @click="simple = false" />
        </template>

        <template #general>
          <UFormGroup
            label="Nombre"
            name="name"
            :required="isRequired(banks$.schema, 'name')"
          >
            <InputText
              v-model.lowercase="state.name"
              autofocus
              :disabled="disabledFields?.includes('name')"
            />
          </UFormGroup>

          <UFormGroup
            label="Código SWIFT"
            name="swift"
            :required="isRequired(banks$.schema, 'swift')"
          >
            <InputText
              v-model.defaultcase="state.swift"
              type="text"
              :disabled="disabledFields?.includes('swift')"
            />
          </UFormGroup>

          <UFormGroup
            label="Código de banco"
            name="bankCode"
            :required="isRequired(banks$.schema, 'bankCode')"
          >
            <InputText
              v-model.defaultcase="state.bankCode"
              type="text"
              :disabled="disabledFields?.includes('bankCode')"
            />
          </UFormGroup>

          <UFormGroup
            label="Número de sucursal"
            name="subsidiaryNumber"
            :required="isRequired(banks$.schema, 'subsidiaryNumber')"
          >
            <InputText
              v-model.defaultcase="state.subsidiaryNumber"
              type="text"
              :disabled="disabledFields?.includes('subsidiaryNumber')"
            />
          </UFormGroup>
        </template>

        <template #details>
          <UFormGroup
            label="Imagen"
            name="image"
            :required="isRequired(banks$.schema, 'image')"
          >
            <InputImage
              v-model="state.image"
              table-name="banks"
              :record-id="state.id"
            />
          </UFormGroup>

          <UFormGroup
            label="Sitio web"
            name="website"
            :required="isRequired(banks$.schema, 'website')"
          >
            <UInput
              v-model="state.website"
              type="url"
              :disabled="disabledFields?.includes('website')"
            />
          </UFormGroup>

          <UFormGroup
            label="Teléfono (sucursal)"
            name="phone"
            :required="isRequired(banks$.schema, 'phone')"
          >
            <InputPhone
              v-model="state.phone"
              :disabled="disabledFields?.includes('phone')"
              country-code="MX"
            />
          </UFormGroup>

          <UFormGroup label="Etiquetas" name="tags">
            <SelectMenuCreatableString
              v-model="state.tags"
              :options="state.tags"
              creatable
            />
          </UFormGroup>

          <UFormGroup label="Archivos" name="files">
            <InputFiles
              v-model="state.files"
              table-name="banks"
              :record-id="state.id"
              accept="*"
            />
          </UFormGroup>
        </template>

        <template #address>
          <FieldsetAddress
            v-model="state.address"
            v-model:name="state.name"
            v-bind="{ schema: banks$.schema, path: 'address', disabledFields }"
          />
        </template>

        <template #data>
          <FieldsetData v-model="state.data" :utils="utils" />
        </template>
      </UAccordion>
    </UForm>

    <template v-if="!inert" #footer>
      <div class="grid grid-cols-2 gap-2 w-full">
        <UButton
          color="gray"
          label="Cancelar"
          block
          @click="slideover.close()"
        />
        <UButton
          form="form.banks"
          type="submit"
          label="Confirmar"
          color="primary"
          block
          :loading="loading"
        />
      </div>
    </template>
  </UDashboardSlideover>
</template>
